/**
 * Where this device takes from (B3): the bar takes from its fridge, though the cellar holds more.
 * Remembered per article and the storage of the last withdrawal, in this browser only; without
 * storage everything works, the suggestion is then where most of it is.
 */
const KEY = "lagerlog.takeFrom";
const MAX = 300;

interface Memory {
  /** Article id → location id of its last withdrawal on this device, oldest first. */
  articles: Record<string, string>;
  /** The storage of the last withdrawal on this device. */
  lager?: string;
}

function read(): Memory {
  try {
    const m = JSON.parse(localStorage.getItem(KEY) ?? "null") as Memory | null;
    return m && typeof m.articles === "object" ? m : { articles: {} };
  } catch {
    return { articles: {} };
  }
}

/** Notes a withdrawal: the location for this article, and its storage for all. */
export function rememberTake(article: string, location: string, lager: string) {
  const m = read();
  delete m.articles[article];
  m.articles[article] = location;
  const ids = Object.keys(m.articles);
  for (const id of ids.slice(0, Math.max(0, ids.length - MAX))) delete m.articles[id];
  m.lager = lager;
  try {
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    // Private mode or full: only the suggestion is lost.
  }
}

/**
 * The location to take an article from: where this device took it last, if there is stock; else
 * the fullest place in the storage this device took from last; else none (the caller decides).
 */
export function takeFrom(article: string, places: readonly { id: string; lager: string }[], stock: (location: string) => number): string | null {
  const m = read();
  const last = m.articles[article];
  if (last && places.some((p) => p.id === last) && stock(last) > 0) return last;
  let best: string | null = null;
  let most = 0;
  for (const p of places) {
    if (p.lager !== m.lager) continue;
    const q = stock(p.id);
    if (q > most) [best, most] = [p.id, q];
  }
  return best;
}
